import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography,
  Grid, Skeleton, LinearProgress
} from '@mui/material';
import {
  People, LocalDrink,
  AccountBalance, Warning
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import api from '../api/authApi';
import SmsSyncBanner from '../components/SmsSyncBanner';

// ── Stat Card Component ──────────────────────────────
function StatCard({ icon, label, value, sub, color, loading }) {
  return (
    <Card sx={{
      borderRadius: 3,
      boxShadow:    '0 2px 12px rgba(0,0,0,0.07)',
      height:       '100%',
      border:       `1.5px solid ${color}22`
    }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{
            width:        44,
            height:       44,
            borderRadius: 2,
            background:   `${color}18`,
            display:      'flex',
            alignItems:   'center',
            justifyContent: 'center'
          }}>
            {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
          </Box>
        </Box>
        {loading
          ? <Skeleton width={80} height={36} />
          : <Typography variant="h4" sx={{ fontWeight: 800, color: '#222', lineHeight: 1 }}>
              {value ?? '—'}
            </Typography>
        }
        <Typography variant="body2" sx={{ color: '#888', mt: 0.5, fontWeight: 600 }}>
          {label}
        </Typography>
        {sub && (
          <Typography variant="caption" sx={{ color }}>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { theme } = useApp();
  const { user }    = useAuth();
  const [stats,     setStats]     = useState(null);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/farmer/stats');
        if (res.data.success) setStats(res.data.data);
      } catch {}
      finally { setLoading(false); }
    };
    fetchStats();
  }, []);

  const trialDaysLeft = user?.planCode === 'TRIAL' ? 7 : null;

  return (
    <Layout title="Dashboard">
      <SmsSyncBanner />

      {/* ── Trial Banner ── */}
      {trialDaysLeft && (
        <Box sx={{
          mb:           2,
          p:            1.5,
          background:   '#fff8f0',
          border:       '1px solid #fed7aa',
          borderRadius: 2,
          display:      'flex',
          alignItems:   'center',
          gap:          1
        }}>
          <Warning sx={{ color: '#f97316' }} />
          <Typography variant="body2" sx={{ color: '#9a3412', fontWeight: 600 }}>
            You are on <strong>7 Days Free Trial</strong>.
            Upgrade to continue using DairyPro after trial ends.
          </Typography>
        </Box>
      )}

      {/* ── Welcome ── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{
          fontWeight: 800,
          color:      '#1a1a1a',
          fontFamily: 'Poppins, sans-serif'
        }}>
          Welcome, {user?.fullName?.split(' ')[0]}! 👋
        </Typography>
        <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>
          {new Date().toLocaleDateString('en-IN', {
            weekday: 'long', year: 'numeric',
            month: 'long', day: 'numeric'
          })}
        </Typography>
      </Box>

      {/* ── Stat Cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<People />}
            label="Total Farmers"
            value={stats?.totalFarmers}
            sub={`Active: ${stats?.activeFarmers ?? 0}`}
            color={theme.primary}
            loading={loading}
          />
        </Grid>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<People />}
            label="Active Farmers"
            value={stats?.activeFarmers}
            sub={`Inactive: ${stats?.inactiveFarmers ?? 0}`}
            color={theme.hover}
            loading={loading}
          />
        </Grid>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<LocalDrink />}
            label="Today's Collection"
            value="0 L"
            sub="No entries today"
            color="#3b82f6"
            loading={false}
          />
        </Grid>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<AccountBalance />}
            label="Pending Payment"
            value="₹0"
            sub="All payments cleared"
            color="#f97316"
            loading={false}
          />
        </Grid>
      </Grid>

      {/* ── Farmer Capacity ── */}
      {stats && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', mb: 3 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#333' }}>
                Farmer Capacity
              </Typography>
              <Typography variant="body2" sx={{ color: theme.primary, fontWeight: 700 }}>
                {stats.totalFarmers} / {stats.maxAllowed}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={stats.maxAllowed > 0
                ? Math.min((stats.totalFarmers / stats.maxAllowed) * 100, 100)
                : 0}
              sx={{
                height:       8,
                borderRadius: 4,
                background:   theme.light,
                '& .MuiLinearProgress-bar': {
                  background:   `linear-gradient(135deg,${theme.primary},${theme.hover})`,
                  borderRadius: 4
                }
              }}
            />
            <Typography variant="caption" sx={{ color: '#aaa', mt: 0.5, display: 'block' }}>
              {stats.maxAllowed - stats.totalFarmers} slots remaining in your plan
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* ── Quick Actions ── */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
        <CardContent sx={{ p: 2.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#333', mb: 2 }}>
            Quick Actions
          </Typography>
          <Grid container spacing={1.5}>
            {[
              { label: 'Add Farmer', icon: '👤', path: '/farmers',     color: theme.primary },
              { label: 'Milk Entry', icon: '🥛', path: '/collections', color: '#3b82f6' },
              { label: 'Rate Chart', icon: '📊', path: '/rate-chart',  color: '#8b5cf6' },
              { label: 'Reports',    icon: '📋', path: '/reports',     color: '#f97316' },
            ].map((item) => (
              <Grid xs={6} sm={3} key={item.label}>
                <Box
                  onClick={() => {}}
                  sx={{
                    p:            2,
                    borderRadius: 2,
                    border:       `1.5px solid ${item.color}22`,
                    background:   `${item.color}08`,
                    cursor:       'pointer',
                    textAlign:    'center',
                    transition:   'all 0.2s',
                    '&:hover': {
                      background: `${item.color}15`,
                      transform:  'translateY(-2px)',
                      boxShadow:  `0 4px 12px ${item.color}22`
                    }
                  }}
                >
                  <Typography variant="h5">{item.icon}</Typography>
                  <Typography variant="caption" sx={{
                    color:      item.color,
                    fontWeight: 700,
                    display:    'block',
                    mt:         0.5
                  }}>
                    {item.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

    </Layout>
  );
}
